import { ONBOARDING_FIELDS, type OnboardingField, type OnboardingUser, type User, UserSchema } from "@fitties/shared";
import { calculateAge } from "@shared/utils/ageUtils";

/**
 * Type guard to check if user has completed profile
 */
export function isCompleteUser(user: OnboardingUser | User): user is User {
  return UserSchema.safeParse(user).success;
}

/**
 * Type guard to check if user needs to complete profile
 */
export function isOnboardingUser(user: OnboardingUser | User): user is OnboardingUser {
  return !isCompleteUser(user);
}

/**
 * Checks if user is at least 18 years old based on date of birth
 * Handles both Date objects and DatabaseTimestampSchema format
 */
export function isAtLeast18YearsOld(birthDate: Date | null | undefined): boolean {
  if (!birthDate) {
    return false;
  }
  const age = calculateAge(birthDate);

  if (age === null) {
    return false;
  }

  return age >= 18;
}

/**
 * Get the next step in onboarding wizard based on schema validation
 * Uses the schema as the source of truth for required fields
 */
export function getNextOnboardingStep(user: OnboardingUser): OnboardingField | null {
  for (const field of ONBOARDING_FIELDS) {
    if (!user[field]) {
      return field;
    }
  }
  return null;
}
/**
 * Check if user can use the main app (profile is complete)
 */
export function canFindMatches(user: OnboardingUser | User): user is User {
  return isCompleteUser(user) && isAtLeast18YearsOld(user.date_of_birth);
}
